import { Input } from '@/components/ui/input';
import { Link } from 'react-router';
import { createPortal } from 'react-dom';
import { ROUTE } from '@/routes/constants';
import { useCallback, useState } from 'react';
import { Role, SCHOOLS } from '@/utils/constants';
import { registerStudent, registerTeacher, requestVerification, verifyRecaptcha } from '@/services/generatedAPIs';
import { generateSalt, generateVerificationToken, hashPassword } from '@/lib/crypto';
import { toast } from 'sonner';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { isEN } from '@/utils/i18n';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAsyncAction } from '@/hooks/use-async-action';
import { useAutoHideScroll } from '@/hooks/use-auto-hide-scroll';
import { useTranslation } from 'react-i18next';
import { ThemeColors } from './login-theme';

// 手机横屏(844×390)「帳戶註冊」右侧表单列：标题固定不滚动,字段较多故整块字段区
// 装进可局部滚动的表单体(overflow-y:auto)。逻辑与桌面/竖屏版完全一致(角色/性别/学校/验证码/reCAPTCHA/注册)。
// reCAPTCHA 弹层用 portal 挂到 body,避开外层舞台的 transform:scale 缩放。

interface Props {
  colors: ThemeColors;
}

export default function MobileLandscapeSignupForm({ colors }: Props) {
  const { t, i18n } = useTranslation();
  const [signupMessage, setSignupMessage] = useState<string>('');
  const [role, setRole] = useState<string>(Role.STUDENT);
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  const [sex, setSex] = useState('X');
  const [email, setEmail] = useState<string>('');
  const [showRecaptcha, setShowRecaptcha] = useState<boolean>(false);
  const [schoolName, setSchoolName] = useState<string>('');
  const scrollRef = useAutoHideScroll<HTMLDivElement>();

  function onSignup(formData: FormData) {
    if (formData.get('password') !== formData.get('confirm-password')) {
      setSignupMessage(t('user.confirm-password-mismatch'));
      return;
    }
    function onOK() { alert(t('user.sign-up-success')); setSignupMessage(t('user.sign-up-success')); }
    function onFail() { setSignupMessage(t('user.sign-up-failed')); }
    execute(async () => {
      const salt = generateSalt();
      if (role === Role.TEACHER) {
        const res = await registerTeacher({
          email: formData.get('email') as string, name: formData.get('name') as string,
          nameEn: formData.get('enname') as string,
          password: hashPassword(formData.get('password') as string + salt),
          salt, schoolName, sex, captcha: formData.get('captcha') as string,
        });
        if (res.code === 200) onOK(); else onFail();
      } else {
        const res = await registerStudent({
          email: formData.get('email') as string, name: formData.get('name') as string,
          nameEn: formData.get('enname') as string,
          password: hashPassword(formData.get('password') as string + salt),
          salt, schoolName,
          classNumber: formData.has('classID') ? Number(formData.get('classID') as string) : undefined,
          schoolNumber: formData.has('schoolID') ? Number(formData.get('schoolID') as string) : undefined,
          gradeLevel: Number(formData.get('grade') as string),
          strn: '', sex, captcha: formData.get('captcha') as string,
        });
        if (res.code === 200) onOK(); else onFail();
      }
    });
  }

  const onVerifyRecaptcha = useCallback(async (value: string | null) => {
    if (!value) return;
    try {
      const rsp = await verifyRecaptcha({ 'g-recaptcha-response': value });
      if (rsp.code === 200 && rsp.data && email) {
        const rsp2 = await requestVerification({ t: generateVerificationToken(email) });
        if (rsp2.code === 200) { toast(t('component.sendsuccess')); return; }
        toast(rsp2.message);
        return;
      }
      toast(t('common.apierror'));
    } catch {
      toast(t('common.apierror'));
    } finally {
      setShowRecaptcha(false);
    }
  }, [email, t]);

  const isStudent = role === Role.STUDENT;
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 500, color: '#575C60', marginBottom: 5 };
  const inputStyle: React.CSSProperties = { width: '100%', height: 28, borderRadius: 10, background: colors.inputBg, border: 0, padding: '0 12px', fontSize: 11, color: '#1F1F1F' };
  const pillStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, height: 30, borderRadius: 15, border: 0, cursor: 'pointer',
    background: active ? '#9CEA4F' : colors.inputBg, color: active ? '#000' : '#575C60',
    fontSize: 12, fontWeight: 500,
  });
  const star = <span style={{ color: '#E5484D' }}>*</span>;

  return (
    <>
      {showRecaptcha && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-6">
          <ReCAPTCHA sitekey={import.meta.env.VITE_RECAPTCHA_SITEKEY} onChange={onVerifyRecaptcha} />
        </div>,
        document.body,
      )}

      <form
        action={onSignup}
        style={{ position: 'absolute', left: 524, top: 60, width: 203, height: 285, display: 'flex', flexDirection: 'column' }}
      >
        {/* 标题(固定,不随表单体滚动) */}
        <h2 style={{ flex: '0 0 auto', margin: 0, textAlign: 'center', fontSize: 20, fontWeight: 600, color: '#2A2F32', lineHeight: '24px', letterSpacing: '-0.01em' }}>
          {t('user.signup-title')}
        </h2>

        {/* 表单体(字段较多,局部滚动;细滚动条仅滚动时显现) */}
        <div ref={scrollRef} className="ml-thin-scroll" style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', marginTop: 10, paddingRight: 4, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, color: '#575C60' }}>{t('user.role-desc')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setRole(Role.STUDENT)} style={pillStyle(isStudent)}>{t('user.student')}</button>
            <button type="button" onClick={() => setRole(Role.TEACHER)} style={pillStyle(!isStudent)}>{t('user.teacher')}</button>
          </div>

          {signupMessage && <p style={{ margin: 0, fontSize: 11, color: '#EF4444', lineHeight: '15px' }}>{signupMessage}</p>}

          <div style={{ fontSize: 11, color: '#575C60' }}>{t('user.info-desc')}</div>

          <div>
            <label htmlFor="ml-name" style={labelStyle}>{t('user.name')}{star}</label>
            <Input id="ml-name" name="name" type="text" required style={inputStyle} />
          </div>

          <div>
            <label htmlFor="ml-enname" style={labelStyle}>{t('user.enname')}{star}</label>
            <Input id="ml-enname" name="enname" type="text" required style={inputStyle} />
          </div>

          <div>
            <label htmlFor="ml-sex" style={labelStyle}>{t('user.sex')}{star}</label>
            <Select name="sex" value={sex} onValueChange={setSex} required>
              <SelectTrigger style={inputStyle}><SelectValue id="ml-sex" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="X">{t('common.nottosay')}</SelectItem>
                <SelectItem value="male">{t('common.male')}</SelectItem>
                <SelectItem value="female">{t('common.female')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label htmlFor="ml-schoolname" style={labelStyle}>{t('user.schoolname')}{star}</label>
            <Select name="schoolname" onValueChange={setSchoolName} value={schoolName} required>
              <SelectTrigger style={inputStyle}><SelectValue id="ml-schoolname" /></SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {SCHOOLS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{isEN(i18n) ? s.en : s.zhHK}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {isStudent && (
            <>
              <div>
                <label htmlFor="ml-grade" style={labelStyle}>{t('common.grade')}{star}</label>
                <Select name="grade" required>
                  <SelectTrigger style={inputStyle}><SelectValue id="ml-grade" /></SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="7">{t('teacher.s1')}</SelectItem>
                      <SelectItem value="8">{t('teacher.s2')}</SelectItem>
                      <SelectItem value="9">{t('teacher.s3')}</SelectItem>
                      <SelectItem value="10">{t('teacher.s4')}</SelectItem>
                      <SelectItem value="11">{t('teacher.s5')}</SelectItem>
                      <SelectItem value="12">{t('teacher.s6')}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="ml-classID" style={labelStyle}>{t('common.classID')}</label>
                <Input id="ml-classID" name="classID" type="number" style={inputStyle} />
              </div>

              <div>
                <label htmlFor="ml-schoolID" style={labelStyle}>{t('common.schoolID')}</label>
                <Input id="ml-schoolID" name="schoolID" type="number" style={inputStyle} />
              </div>
            </>
          )}

          <div>
            <label htmlFor="ml-signup-email" style={labelStyle}>{t('user.email')}{star}</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <Input
                id="ml-signup-email" name="email" type="email" required placeholder="m@example.com"
                onInvalid={(e: any) => { e.target.setCustomValidity(t('user.email-error')); }}
                onInput={(e: any) => { e.target.setCustomValidity(''); setEmail(e.target.value); }}
                style={{ ...inputStyle, flex: 1, minWidth: 0 }}
              />
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setShowRecaptcha(true); }}
                style={{ flex: '0 0 auto', height: 28, padding: '0 8px', borderRadius: 10, background: '#9CEA4F', color: '#000', border: 0, fontSize: 10, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {t('component.getcaptcha')}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="ml-captcha" style={labelStyle}>{t('user.captcha')}{star}</label>
            <Input id="ml-captcha" name="captcha" type="text" required style={inputStyle} />
          </div>

          <div>
            <label htmlFor="ml-password" style={labelStyle}>{t('user.password')}{star}</label>
            <Input
              id="ml-password" name="password" type="password" required
              pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
              onInvalid={(e: any) => { e.target.setCustomValidity(t('user.password-error')); }}
              onInput={(e: any) => { e.target.setCustomValidity(''); }}
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="ml-confirm-password" style={labelStyle}>{t('user.confirm-password')}{star}</label>
            <Input
              id="ml-confirm-password" name="confirm-password" type="password" required
              pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
              onInvalid={(e: any) => { e.target.setCustomValidity(t('user.password-error')); }}
              onInput={(e: any) => { e.target.setCustomValidity(''); }}
              style={inputStyle}
            />
          </div>

          <div style={{ fontSize: 10, color: '#575C60', textAlign: 'center', lineHeight: '15px' }}>
            {t('user.create-account-msg')}
            <Link to={ROUTE.PRIVACY_POLICY} style={{ marginLeft: 4, color: colors.link }} className="hover:underline">{t('user.privacypolicy')}</Link>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%', height: 33, borderRadius: 16.5, background: colors.buttonBg, color: colors.buttonText,
              border: 0, fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: isLoading ? 0.6 : 1,
            }}
            className="hover:brightness-[0.97]"
          >
            {t('user.create-account')}
          </button>

          <div style={{ textAlign: 'center', fontSize: 11, color: '#575C60' }}>
            {t('user.signup-comment')}
            <Link to={ROUTE.LOGIN} style={{ marginLeft: 4, color: colors.link }} className="underline underline-offset-2">{t('user.sign-in')}</Link>
          </div>
        </div>
      </form>
    </>
  );
}
